import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircularProgress } from '@mui/material';
import { Formik, FormikActions, FormikValues } from 'formik';

import { getNotificationSettings, updateNotificationSettings } from 'services/clients/notification';

import { NotificationSettings } from 'types/User';
import { useRegion } from 'common/hooks/region';
import FormContent from './FormContent';

export const Form: React.FC = () => {
  const queryClient = useQueryClient();
  const { code: portCollection } = useRegion();
  const notificationSettings = useQuery(['notificationSettings', portCollection], ({ signal }) =>
    getNotificationSettings(portCollection, signal)
  );
  const mutateSettings = useMutation(updateNotificationSettings, {
    mutationKey: ['notificationSettings', portCollection],
    onSuccess: () => {
      queryClient.invalidateQueries(['notificationSettings', portCollection]);
    },
  });

  const handleSubmit = React.useCallback(
    async (values: NotificationSettings, actions: FormikActions<FormikValues>) => {
      try {
        const data = await mutateSettings.mutateAsync({ values });
        actions.setValues(data);
        actions.setSubmitting(false);
        actions.setStatus('success');
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
    [mutateSettings]
  );

  if (notificationSettings.isLoading || !notificationSettings.data)
    return (
      <div className="w-full text-center">
        <CircularProgress />
      </div>
    );

  return (
    <div className="w-full bg-white shadow-sm">
      <Formik
        initialValues={notificationSettings.data}
        onSubmit={handleSubmit}
        initialStatus="pending"
        // This key is necessary to make sure the initial values are updated when switching port collections
        key={portCollection}
      >
        {(props) => <FormContent {...props} />}
      </Formik>
    </div>
  );
};
Form.displayName = 'NotificationSettings.Form';

export default Form;
